import React, { useMemo } from 'react';
import { AiPendingActionPlan, AiPlanMessage, AiPlannerIssue, AiPlannerResponse } from '../services/api';
import { brandClasses } from '../theme';
import { useTranslation } from 'react-i18next';

type AiPlanPreviewCardProps = {
  plan: AiPendingActionPlan;
  plannerResponse?: AiPlannerResponse | null;
  nowUtcMs: number;
  isConfirmed?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const AiPlanPreviewCard: React.FC<AiPlanPreviewCardProps> = ({
  plan,
  plannerResponse,
  nowUtcMs,
  isConfirmed = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const isExpired = new Date(plan.expiresAtUtc).getTime() <= nowUtcMs;
  const hasBlockingErrors = (plannerResponse?.errors ?? []).length > 0;
  const confirmDisabled = isExpired || hasBlockingErrors || plan.status !== 'ReadyForConfirmation' || isConfirmed;
  const confirmReason = isConfirmed
    ? 'Plan marked confirmed locally.'
    : isExpired
      ? 'This plan has expired.'
      : hasBlockingErrors
        ? 'Resolve the blocking errors before confirming.'
        : plan.status !== 'ReadyForConfirmation'
          ? `Plan status is ${plan.status}.`
          : 'Ready to confirm.';

  const timeRemaining = useMemo(() => formatTimeRemaining(plan.expiresAtUtc, nowUtcMs), [nowUtcMs, plan.expiresAtUtc]);

  return (
    <div className={`rounded-[28px] border p-4 shadow-[0_10px_28px_rgba(95,83,58,0.10)] ${brandClasses.appShell}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">{t('plan.pendingActionPlan', { ns: 'ai' })}</p>
          <h3 className="mt-1 text-lg font-semibold text-[#1A1714]">{plan.title}</h3>
          <p className="mt-1 text-sm text-[#6B6560]">{plan.summary}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={isExpired ? 'danger' : isConfirmed ? 'success' : 'neutral'}>
            {isExpired ? 'Expired' : isConfirmed ? 'Confirmed locally' : plan.status}
          </Badge>
          <Badge tone={isExpired ? 'danger' : 'neutral'}>{timeRemaining}</Badge>
        </div>
      </div>

      {plannerResponse?.summary && (
        <p className="mt-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2 text-sm text-[#4A443E]">
          {plannerResponse.summary}
        </p>
      )}

      {plannerResponse?.errors?.length ? (
        <Section title={t('plan.blockingErrors', { ns: 'ai' })} tone="danger">
          <MessageList messages={plannerResponse.errors} />
        </Section>
      ) : null}

      {plan.warnings.length > 0 ? (
        <Section title={t('plan.warnings', { ns: 'ai' })} tone="warning">
          <MessageList messages={plan.warnings} />
        </Section>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.25fr_0.75fr]">
        <Section title={t('plan.resolvedEntities', { ns: 'ai' })} tone="neutral">
          {plan.resolvedEntities.length > 0 ? (
            <div className="space-y-2">
              {plan.resolvedEntities.map((entity) => (
                <div key={`${entity.entityType}-${entity.entityId}`} className="rounded-2xl border border-[#E2DDD8] bg-white px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-[#1A1714]">{entity.displayName}</p>
                      <p className="text-xs text-[#6B6560]">{entity.entityType}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <Badge tone={entity.isExactMatch ? 'success' : 'warning'}>{entity.isExactMatch ? 'Exact' : 'Approx'}</Badge>
                      <Badge tone="neutral">{Math.round(entity.confidence * 100)}%</Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState text={t('plan.noResolvedEntities', { ns: 'ai' })} />
          )}
        </Section>

        <Section title={t('plan.writeSteps', { ns: 'ai' })} tone="neutral">
          {plan.steps.length > 0 ? (
            <div className="space-y-2">
              {plan.steps
                .slice()
                .sort((left, right) => left.order - right.order)
                .map((step) => (
                  <div key={`${step.order}-${step.title}`} className={`rounded-2xl border px-3 py-3 ${step.isBlocking ? 'border-[#E7B7B7] bg-[#FFF7F7]' : 'border-[#E2DDD8] bg-white'}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-[#1A1714]">{step.order}. {step.title}</p>
                      <Badge tone={step.isBlocking ? 'danger' : 'neutral'}>{step.isBlocking ? 'Blocking' : 'Non-blocking'}</Badge>
                    </div>
                    {step.description && <p className="mt-1 text-sm text-[#6B6560]">{step.description}</p>}
                    {step.toolCallId && <p className="mt-1 text-xs text-[#8D867E]">Tool call {step.toolCallId}</p>}
                  </div>
                ))}
            </div>
          ) : (
            <EmptyState text={t('plan.noStepsGenerated', { ns: 'ai' })} />
          )}
        </Section>
      </div>

      <Section title={t('plan.plannedTools', { ns: 'ai' })} tone="neutral" className="mt-4">
        {plan.plannedToolCalls.length > 0 ? (
          <div className="space-y-2">
            {plan.plannedToolCalls.map((toolCall) => (
              <div key={toolCall.toolCallId} className="rounded-2xl border border-[#E2DDD8] bg-white px-3 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#1A1714]">{toolCall.toolName}</p>
                    <p className="text-xs text-[#6B6560]">Kind {toolCall.kind}</p>
                  </div>
                  <Badge tone={toolCall.requiresConfirmation ? 'warning' : 'neutral'}>{toolCall.requiresConfirmation ? 'Needs confirmation' : 'Auto'}</Badge>
                </div>
                <pre className="mt-2 overflow-x-auto rounded-xl bg-[#F8F5EF] p-3 text-xs text-[#6B6560]">
                  {JSON.stringify(toolCall.arguments, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState text={t('plan.noToolCallsPlanned', { ns: 'ai' })} />
        )}
      </Section>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3">
        <div>
          <p className="text-sm font-medium text-[#1A1714]">{t('plan.confirmationLabel', { ns: 'ai' })}</p>
          <p className="text-xs text-[#6B6560]">{confirmReason}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[#DDD7CE] px-3 py-2 text-sm text-[#4A443E] transition hover:bg-[#F3EEE7]"
          >
            Cancel plan
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="rounded-xl bg-[#185FA5] px-3 py-2 text-sm font-medium text-white transition hover:bg-[#0C447C] disabled:cursor-not-allowed disabled:bg-[#9E9890]"
          >
            Confirm plan
          </button>
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; tone: 'neutral' | 'warning' | 'danger'; className?: string; children: React.ReactNode }> = ({
  title,
  tone,
  className,
  children,
}) => {
  const toneClasses = {
    neutral: 'border-[#E2DDD8] bg-[#FFFDF9]',
    warning: 'border-[#F0D2A8] bg-[#FFFAF2]',
    danger: 'border-[#E7B7B7] bg-[#FFF4F4]',
  }[tone];

  return (
    <div className={`mt-4 rounded-[24px] border p-4 ${toneClasses} ${className ?? ''}`.trim()}>
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8D867E]">{title}</p>
      <div className="mt-3">{children}</div>
    </div>
  );
};

const Badge: React.FC<{ tone: 'neutral' | 'success' | 'warning' | 'danger'; children: React.ReactNode }> = ({ tone, children }) => {
  const toneClasses = {
    neutral: 'border-[#DDD7CE] bg-white text-[#4A443E]',
    success: 'border-[#BFD1A4] bg-[#F7FBF0] text-[#4A443E]',
    warning: 'border-[#F0D2A8] bg-[#FFFAF2] text-[#4A443E]',
    danger: 'border-[#E7B7B7] bg-[#FFF4F4] text-[#8C2F2F]',
  }[tone];

  return <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${toneClasses}`}>{children}</span>;
};

type PlanMessage = AiPlannerIssue | AiPlanMessage;

const MessageList: React.FC<{ messages: PlanMessage[] }> = ({ messages }) => (
  <div className="space-y-2">
    {messages.map((message) => (
      <div key={message.code} className="rounded-2xl border border-[#E2DDD8] bg-white px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-[#1A1714]">{message.message}</p>
            <p className="mt-1 text-xs text-[#6B6560]">{message.code}</p>
          </div>
          <Badge tone={severityTone(getMessageSeverity(message))}>{getMessageSeverity(message)}</Badge>
        </div>
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <p className="rounded-2xl border border-dashed border-[#D7D3CC] bg-[#FBFAF7] px-3 py-3 text-sm text-[#6B6560]">{text}</p>
);

const severityTone = (severity: 'Info' | 'Warning' | 'Error') => {
  switch (severity) {
    case 'Error':
      return 'danger';
    case 'Warning':
      return 'warning';
    default:
      return 'neutral';
  }
};

const getMessageSeverity = (message: PlanMessage): 'Info' | 'Warning' | 'Error' => {
  if ('severity' in message) {
    return message.severity;
  }

  switch (message.category) {
    case 'Permission':
    case 'Validation':
    case 'Conflict':
      return 'Error';
    case 'NotFound':
    case 'Expired':
    case 'Unsupported':
      return 'Warning';
    default:
      return 'Info';
  }
};

const formatTimeRemaining = (expiresAtUtc: string, nowUtcMs: number) => {
  const expiresAt = new Date(expiresAtUtc).getTime();
  const remainingMs = expiresAt - nowUtcMs;
  if (remainingMs <= 0) {
    return 'Expired';
  }

  const totalMinutes = Math.max(1, Math.round(remainingMs / 60000));
  if (totalMinutes < 60) {
    return `${totalMinutes}m remaining`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0 ? `${hours}h ${minutes}m remaining` : `${hours}h remaining`;
};
